// ==============================
// QuickActions
// ==============================
// Encapsulates the four "quick access" flows that originally lived only on the
// Home page (New User, New Organization, Enroll existing user/org, Trial
// Booking) — all of their form state, validation, networking and dialogs.
//
// Consumers render their own trigger UI (Home's cards, or a standard button on
// a feature screen) through the render-prop `children`, which receives the four
// open handlers. The dialogs themselves are rendered internally, so every
// screen gets exactly the same behavior as the Home quick-access cards.
//
// Note: this component intentionally does NOT render a <ToastContainer/> — a
// single global one is mounted app-wide in App.tsx.

// ==============================
// Imports
// ==============================

// MUI Components
import {
  Autocomplete,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Libraries
import { z } from 'zod';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

// Local Components
import ReuseableModal from './ReuseableModal';
import ServiceAccordion from './ServiceAccordion';
import { apiGet, apiPost } from '../utils/apiClient';
import {
  CreateTrialBookingRequest,
  CreateTrialBookingResponse,
  Response,
  ServiceType,
  Subtype,
} from '../types/TrialBookingTable.type';

// Hooks
import useModal from '../hooks/useModal';

// Utils
import { notifyAPIClient } from '../utils/toast';

// ==============================
// Types
// ==============================
interface AutofillResponse {
  data: ExistedUser[];
}

// Create new user types
type Gender = 'MALE' | 'FEMALE' | 'UNDEFINED';

type CreateUserRequest = {
  firstName: string;
  lastName: string;
  preferredName: string;
  businessName: string;
  dob: string | undefined;
  gender?: Gender;
  userType?: 'ADULT' | 'CHILD' | 'BUSINESS';
};

type CreateUserResponse = {
  status: number;
  data: ExistedUser;
  message: string;
};

type ExistedUser = {
  firstName: string;
  lastName: string;
  id: string;
  fullName: string;
  dob: string;
  userType: 'ADULT' | 'CHILD' | 'BUSINESS';
  phone?: string | null;
};

// Handlers exposed to consumers so they can render their own trigger UI.
export type QuickActionHandlers = {
  // Register a brand-new individual customer.
  openNewUser: () => void;
  // Register a new business / organization.
  openNewOrg: () => void;
  // Enroll a service for an existing customer.
  openExistedUser: () => void;
  // Book a trial service for a prospect.
  openTrialBooking: () => void;
  // Start service registration for a specific already-existing customer
  // (used by the dashboard search bar — "found = existing").
  startServiceForExistingUser: (user: {
    id: number | string;
    fullName: string;
    userType: string;
    dob?: string;
    // Value the admin searched by, used to prefill the registration dialog.
    phone?: string;
    email?: string;
  }) => void;
};

interface QuickActionsProps {
  children: (handlers: QuickActionHandlers) => React.ReactNode;
}

// Accent-insensitive normalizer for Vietnamese: strips diacritics and maps
// đ/Đ, so "Do Quang Dung" matches "Đỗ Quang Dũng".
const normalizeText = (value: string | null | undefined) =>
  (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/\s+/g, ' ') // collapse double/irregular spaces (e.g. "PARK  HYEWON")
    .trim();

// ==============================
// Forms Validation
// ==============================
// Create new user validation
const CreateUserInput = z.object({
  firstName: z
    .string()
    .min(1, 'Please enter first name')
    .refine((val) => val.length === 0 || val.trim().length > 0, {
      message: 'First name cannot be just whitespace',
    }),
  lastName: z
    .string()
    .min(1, 'Please enter last name')
    .refine((val) => val.length === 0 || val.trim().length > 0, {
      message: 'Last name cannot be just whitespace',
    }),
  preferredName: z.string().optional(),
  businessName:
    localStorage.getItem('selectedUserType') === 'BUSINESS'
      ? z.string().min(1, 'Please enter business name')
      : z.string().optional(),
  dob: z
    .string({
      required_error: 'Please enter date of birth',
    })
    .date('Please enter a valid date'),
  gender: z.enum(['MALE', 'FEMALE', 'UNDEFINED']).optional(),
  userType: z.string().optional().nullable(), // Add nullable
});
type CreateUserInputType = z.infer<typeof CreateUserInput>;
type FormDataErrors = {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  businessName?: string;
  dob?: string;
  gender?: string;
};

// Create trial booking types
const CreateTrialBookingInput = z.object({
  firstName: z.string().min(1, 'Please enter first name'),
  lastName: z.string().min(1, 'Please enter last name'),
  email: z
    .string()
    .email('Please enter a valid email')
    .optional()
    .nullable()
    .or(z.literal('')),
  phone: z.string().min(1, 'Please enter phone number'),
  parent: z.string().optional(),
  dob: z
    .string({
      required_error: 'Please enter date of birth',
    })
    .date('Please enter a valid date'),
  subtypeId: z
    .number({
      required_error: 'Please select a program',
    })
    .min(1, 'Please select a program'),
  trialDate: z
    .string({
      required_error: 'Please select a trial date',
    })
    .datetime({ local: true, message: 'Please enter a valid date' }),
  note: z.string().optional(),
});
type CreateTrialBookingInputType = z.infer<typeof CreateTrialBookingInput>;
type FormDataTrialBookingErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  parent?: string;
  dob?: string;
  subtypeId?: string;
  trialDate?: string;
  note?: string;
};

// ==============================
// Component
// ==============================
const QuickActions = ({ children }: QuickActionsProps) => {
  // Modal states
  const { isOpen: isOpenNewUser, toggle: toggleNewUser } = useModal();
  const { isOpen: isOpenExistedUser, toggle: toggleExistedUser } = useModal();
  const {
    isOpen: isOpenServiceRegistration,
    toggle: toggleEServiceRegistration,
  } = useModal();
  const { isOpen: isOpenTrialBooking, toggle: toggleTrialBooking } = useModal();

  // New user form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    businessName: '',
    gender: 'UNDEFINED' as Gender,
  });
  const [formDataErrors, setFormDataErrors] = useState<FormDataErrors>({});

  // Registration service
  const [dob, setDob] = useState<dayjs.Dayjs | null>(dayjs().startOf('day'));

  // Existed user form state
  const [formDataExisted, setFormDataExisted] = useState({
    fullName: '',
    id: '',
    dob: '',
    userType: '',
  });

  // Trial booking form state
  const [formDataTrialBooking, setFormDataTrialBooking] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    parent: '',
    dob: '',
    subtypeId: '',
    trialDate: '',
    note: '',
  });
  const [formDataTrialBookingErrors, setFormDataTrialBookingErrors] =
    useState<FormDataTrialBookingErrors>({});

  const [coursePrograms, setCoursePrograms] = useState<Subtype[]>([]);
  const [trialDob, setTrialDob] = useState<dayjs.Dayjs | null>(
    dayjs().startOf('day'),
  );
  const [trialDate, setTrialDate] = useState<dayjs.Dayjs | null>(
    dayjs().startOf('day'),
  );

  const fetchCoursePrograms = async () => {
    const res = await apiGet<Response<ServiceType>>(
      `/service/filter?name=COURSE`,
    );
    setCoursePrograms(res.data[0].subtypes);
  };
  useEffect(() => {
    if (isOpenTrialBooking) {
      fetchCoursePrograms();
      setTrialDob(dayjs().startOf('day'));
      setTrialDate(dayjs().startOf('day'));
    } else {
      setCoursePrograms([]);
      setFormDataTrialBooking({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        parent: '',
        dob: '',
        subtypeId: '',
        trialDate: '',
        note: '',
      });
      setTrialDate(null);
      setTrialDob(null);
    }

    // Set dob to beg of today everytime open dialog
    if (isOpenNewUser) setDob(dayjs().startOf('day'));
  }, [isOpenTrialBooking, isOpenNewUser]);

  // Existed user data
  const [existedUsers, setExistedUsers] = useState<ExistedUser[]>([]);
  const [hasFetchedUsers, setHasFetchedUsers] = useState(false);

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenderChange = (e: {
    target: { name: string; value: string };
  }) => {
    setFormData({ ...formData, [e.target.name]: e.target.value as Gender });
  };

  const handleInputChangeExisted = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormDataExisted({ ...formDataExisted, [e.target.name]: e.target.value });
  };

  const handleInputChangeTrialBooking = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormDataTrialBooking({
      ...formDataTrialBooking,
      [e.target.name]: e.target.value,
    });
  };

  // Toast
  const notifySuccess = () => toast('User created successfully.');

  // Networking
  const handleSubmit = async () => {
    const payload: CreateUserRequest = {
      ...formData,
      businessName: formData.businessName || '',
      dob:
        localStorage.getItem('selectedUserType') === 'BUSINESS'
          ? new Date().toISOString().split('T')[0] // Default to today for business
          : dob?.format('YYYY-MM-DD'),
      userType:
        localStorage.getItem('selectedUserType') === 'BUSINESS'
          ? 'BUSINESS'
          : undefined,
    };

    try {
      const data = CreateUserInput.parse(payload);

      const res = await apiPost<CreateUserResponse, CreateUserInputType>(
        '/user/create',
        data,
      );

      notifySuccess();
      toggleNewUser();

      setFormData({
        firstName: '',
        lastName: '',
        preferredName: '',
        businessName: '',
        gender: 'UNDEFINED' as Gender,
      });
      setFormDataErrors({});
      setDob(null);

      // Update 01/06/2025 fixed missing data bug
      // Store temporary data
      localStorage.setItem('selectedUserId', res.data.id);
      localStorage.setItem('selectedUserType', res.data.userType);
      localStorage.setItem(
        'selectedUserFullName',
        `${res.data.lastName} ${res.data.firstName}`,
      );
      localStorage.setItem('selectedUserDob', res.data.dob);
      // Not from search — clear any stale searched prefill.
      localStorage.removeItem('selectedSearchPhone');
      localStorage.removeItem('selectedSearchEmail');
      // Open service registration form
      toggleEServiceRegistration();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormDataErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormDataErrors;
          fieldErrors[field] = err.message;
        });
        setFormDataErrors(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient('An error occurred while submitting the form.');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiGet<AutofillResponse>('/user/autofill');

      const cleanUsers = res.data
        .filter((user) => user && user.id && user.fullName && user.dob)
        .map((user) => ({
          ...user,
          id: String(user.id),
          fullName: String(user.fullName),
          dob: String(user.dob),
        }));
      setExistedUsers(cleanUsers);
      setHasFetchedUsers(true);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmitExisted = () => {
    if (!formDataExisted.id) {
      notifyAPIClient('Please select an user to enroll.');
      return;
    }
    toggleExistedUser();
    // Store temporary data
    localStorage.setItem('selectedUserType', formDataExisted.userType);
    localStorage.setItem('selectedUserId', formDataExisted.id);
    localStorage.setItem('selectedUserFullName', formDataExisted.fullName);
    localStorage.setItem('selectedUserDob', formDataExisted.dob);
    // Not from search — clear any stale searched prefill.
    localStorage.removeItem('selectedSearchPhone');
    localStorage.removeItem('selectedSearchEmail');

    // Open service registration form
    toggleEServiceRegistration();
  };

  const handleSubmitTrialBooking = async () => {
    const payload: CreateTrialBookingRequest = {
      ...formDataTrialBooking,
      subtypeId: Number(formDataTrialBooking.subtypeId),
      dob: trialDob?.format('YYYY-MM-DD'),
      trialDate: trialDate?.format('YYYY-MM-DDTHH:mm:ss'),
    };

    try {
      const data = CreateTrialBookingInput.parse(payload);
      const res = await apiPost<
        CreateTrialBookingResponse,
        CreateTrialBookingInputType
      >('/trial-booking/create', data);

      notifyAPIClient(res.message);
      setFormDataTrialBooking({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        parent: '',
        dob: '',
        subtypeId: '',
        trialDate: '',
        note: '',
      });
      setFormDataTrialBookingErrors({});
      setTrialDate(null);
      setTrialDob(null);

      // Close the modal
      toggleTrialBooking();
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: FormDataTrialBookingErrors = {};
        err.errors.forEach((error) => {
          const field = error.path[0] as keyof FormDataTrialBookingErrors;
          fieldErrors[field] = error.message;
        });
        setFormDataTrialBookingErrors(fieldErrors);
      } else {
        console.error(err);
        notifyAPIClient('An error occurred while submitting the form.');
      }
    }
  };

  useEffect(() => {
    if (isOpenExistedUser && !hasFetchedUsers) {
      fetchUsers();
    }
  }, [isOpenExistedUser, hasFetchedUsers]);

  // Trigger handlers exposed to consumers — same behavior as the Home cards.
  const handlers: QuickActionHandlers = {
    openNewUser: () => {
      localStorage.removeItem('selectedUserType');
      toggleNewUser();
    },
    openNewOrg: () => {
      localStorage.setItem('selectedUserType', 'BUSINESS');
      toggleNewUser();
    },
    openExistedUser: toggleExistedUser,
    openTrialBooking: toggleTrialBooking,
    startServiceForExistingUser: (user) => {
      localStorage.setItem('selectedUserType', user.userType || '');
      localStorage.setItem('selectedUserId', String(user.id));
      localStorage.setItem('selectedUserFullName', user.fullName);
      localStorage.setItem('selectedUserDob', user.dob || '');
      // Carry the searched phone/email into the registration dialog prefill.
      localStorage.setItem('selectedSearchPhone', user.phone || '');
      localStorage.setItem('selectedSearchEmail', user.email || '');
      toggleEServiceRegistration();
    },
  };

  // ==============================
  // Render
  // ==============================
  return (
    <>
      {/* Consumer-provided trigger UI (cards, buttons, ...) */}
      {children(handlers)}

      {/* Dialog 1 */}
      {/* New User Modal */}
      <ReuseableModal
        isOpen={isOpenNewUser}
        toggle={toggleNewUser}
        width="w-2/5"
        height="h-max"
      >
        <div className="p-4">
          <Typography variant="h6" color="secondary" fontWeight="bold">
            {localStorage.getItem('selectedUserType') === 'BUSINESS'
              ? 'Register new business user form'
              : 'Register new user form'}
          </Typography>
          <Typography variant="body1" color="text.accent" sx={{ mt: 1, mb: 1 }}>
            Fill all required information below to create new user.
          </Typography>
          <div className="flex flex-col gap-2">
            <TextField
              id="tf-lastname"
              className="required-field"
              label="Last Name"
              variant="standard"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              error={Boolean(formDataErrors.lastName)}
              helperText={formDataErrors.lastName}
            />
            <TextField
              id="tf-firstname"
              className="required-field"
              label="First Name"
              variant="standard"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              error={Boolean(formDataErrors.firstName)}
              helperText={formDataErrors.firstName}
            />
            {localStorage.getItem('selectedUserType') === 'BUSINESS' ? (
              <TextField
                id="tf-businessName"
                className="required-field"
                label="Business Name"
                variant="standard"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                error={Boolean(formDataErrors.businessName)}
                helperText={formDataErrors.businessName}
              />
            ) : (
              <>
                <TextField
                  id="tf-preferredname"
                  label="Preferred Name"
                  variant="standard"
                  name="preferredName"
                  value={formData.preferredName}
                  onChange={handleInputChange}
                  error={Boolean(formDataErrors.preferredName)}
                  helperText={formDataErrors.preferredName}
                />
                <FormControl
                  variant="standard"
                  error={Boolean(formDataErrors.gender)}
                  fullWidth
                  className="!my-4"
                >
                  <InputLabel id="gender-label">Gender</InputLabel>
                  <Select
                    labelId="gender-label"
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleGenderChange}
                  >
                    <MenuItem value="MALE">Male</MenuItem>
                    <MenuItem value="FEMALE">Female</MenuItem>
                    <MenuItem value="UNDEFINED">Undefined</MenuItem>
                  </Select>
                  {formDataErrors.gender && (
                    <FormHelperText>{formDataErrors.gender}</FormHelperText>
                  )}
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Date of Birth"
                    value={dob}
                    onOpen={() => setDob(dayjs().startOf('day'))}
                    onChange={(newDate) => setDob(newDate)}
                  />
                  {/* Error handling for date of birth */}
                  {formDataErrors.dob && (
                    <Typography variant="caption" color="error">
                      {formDataErrors.dob}
                    </Typography>
                  )}
                </LocalizationProvider>
              </>
            )}

            <div className="flex justify-end gap-4">
              <Button
                variant="contained"
                className="!text-white"
                color="error"
                onClick={() => {
                  toggleNewUser();
                  setFormData({
                    firstName: '',
                    lastName: '',
                    preferredName: '',
                    businessName: '',
                    gender: 'UNDEFINED' as Gender,
                  });
                  setDob(null);
                  setFormDataErrors({});
                  localStorage.removeItem('selectedUserType');
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className="!text-white"
                color="primary"
                onClick={handleSubmit}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </ReuseableModal>

      {/* Dialog 2 */}
      {/* Existed User Modal */}
      <ReuseableModal
        isOpen={isOpenExistedUser}
        toggle={toggleExistedUser}
        width="w-2/5"
        height="h-max"
      >
        <div className="p-4">
          <Typography variant="h6" color="secondary" fontWeight="bold">
            Existed user enroll form
          </Typography>
          <Typography variant="body1" color="text.accent" sx={{ mt: 1, mb: 1 }}>
            Fill all required information below to enroll.
          </Typography>
          <div className="flex flex-col gap-2">
            <Autocomplete
              options={existedUsers}
              getOptionLabel={(option) =>
                option?.id && option?.fullName
                  ? `${option.id} - ${option.fullName}`
                  : ''
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options, state) => {
                const input = normalizeText(state.inputValue);
                return options.filter((opt) => {
                  const id = normalizeText(opt.id);
                  const name = normalizeText(opt.fullName);
                  const phone = normalizeText(opt.phone);
                  return (
                    id.includes(input) ||
                    name.includes(input) ||
                    phone.includes(input)
                  );
                });
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.phone || 'No phone'} - {option.id} - {option.fullName}
                </li>
              )}
              onChange={(e, newValue) => {
                if (newValue) {
                  setFormDataExisted({
                    fullName: newValue.fullName,
                    id: newValue.id,
                    dob: newValue.dob,
                    userType: newValue.userType,
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="required-field"
                  label="User (Phone - ID - Name)"
                  variant="standard"
                  value={`${formDataExisted.id} - ${formDataExisted.fullName}`}
                  onChange={handleInputChangeExisted}
                  name="autocomplete"
                />
              )}
            />
            <div className="flex justify-end gap-4">
              <Button
                variant="contained"
                className="!text-white"
                color="error"
                onClick={() => {
                  toggleExistedUser();
                  setFormDataExisted({
                    fullName: '',
                    id: '',
                    dob: '',
                    userType: '',
                  });
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className="!text-white"
                color="primary"
                onClick={handleSubmitExisted}
              >
                Enroll
              </Button>
            </div>
          </div>
        </div>
      </ReuseableModal>

      {/* Dialog 3 */}
      {/* Service registration form */}
      <ReuseableModal
        isOpen={isOpenServiceRegistration}
        toggle={toggleEServiceRegistration}
        width="w-3/5"
        height="h-4/5"
      >
        <div className="flex h-full min-h-0 flex-col p-4">
          <Typography variant="h6" color="secondary" fontWeight="bold">
            Service registration form
          </Typography>
          <Typography variant="body1" color="text.accent" >
            Choose service, service type and package to enroll.
          </Typography>
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            {/* Service Accordion */}
            <ServiceAccordion onClose={toggleEServiceRegistration} />
          </div>
        </div>
      </ReuseableModal>

      {/* Dialog 4 */}
      {/* Trial Booking Modal */}
      <ReuseableModal
        isOpen={isOpenTrialBooking}
        toggle={toggleTrialBooking}
        width="w-2/5"
        height="h-max"
        overlayClassName="trial-booking-modal-overlay"
      >
        <div className="p-4">
          <Typography variant="h6" color="secondary" fontWeight="bold">
            Book a trial service
          </Typography>
          <div className="flex flex-col gap-3">
            <TextField
              id="tf-lastname"
              className="required-field"
              label="Last Name"
              variant="standard"
              name="lastName"
              value={formDataTrialBooking.lastName}
              onChange={handleInputChangeTrialBooking}
              error={Boolean(formDataTrialBookingErrors.lastName)}
              helperText={formDataTrialBookingErrors.lastName}
            />

            <TextField
              id="tf-firstname"
              className="required-field"
              label="First Name"
              variant="standard"
              name="firstName"
              value={formDataTrialBooking.firstName}
              onChange={handleInputChangeTrialBooking}
              error={Boolean(formDataTrialBookingErrors.firstName)}
              helperText={formDataTrialBookingErrors.firstName}
            />

            <TextField
              id="tf-email"
              label="Email"
              variant="standard"
              name="email"
              value={formDataTrialBooking.email}
              onChange={handleInputChangeTrialBooking}
              error={Boolean(formDataTrialBookingErrors.email)}
              helperText={formDataTrialBookingErrors.email}
            />

            <TextField
              id="tf-phone"
              className="required-field"
              label="Phone"
              variant="standard"
              name="phone"
              value={formDataTrialBooking.phone}
              onChange={handleInputChangeTrialBooking}
              error={Boolean(formDataTrialBookingErrors.phone)}
              helperText={formDataTrialBookingErrors.phone}
            />

            <TextField
              id="tf-parent"
              label="Parent Name"
              variant="standard"
              name="parent"
              value={formDataTrialBooking.parent}
              onChange={handleInputChangeTrialBooking}
              error={Boolean(formDataTrialBookingErrors.parent)}
              helperText={formDataTrialBookingErrors.parent}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of Birth"
                value={trialDob}
                onChange={(newDate) => setTrialDob(newDate)}
                slotProps={{ textField: { className: 'required-field' } }}
              />
            </LocalizationProvider>
            {formDataTrialBookingErrors.dob && (
              <Typography variant="caption" color="error">
                {formDataTrialBookingErrors.dob}
              </Typography>
            )}

            <FormControl fullWidth>
              <InputLabel id="program-label" className="required-field">
                Program
              </InputLabel>
              <Select
                labelId="program-label"
                id="program"
                value={formDataTrialBooking.subtypeId}
                onChange={(event) => {
                  setFormDataTrialBooking({
                    ...formDataTrialBooking,
                    subtypeId: event.target.value,
                  });
                }}
                label="Program"
              >
                {coursePrograms.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
              {formDataTrialBookingErrors.subtypeId && (
                <Typography variant="caption" color="error">
                  {formDataTrialBookingErrors.subtypeId}
                </Typography>
              )}
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Trial Date"
                value={trialDate}
                onChange={(newDate) => setTrialDate(newDate)}
                slotProps={{ textField: { className: 'required-field' } }}
              />
            </LocalizationProvider>
            {formDataTrialBookingErrors.trialDate && (
              <Typography variant="caption" color="error">
                {formDataTrialBookingErrors.trialDate}
              </Typography>
            )}

            <TextField
              id="tf-note"
              label="Note"
              name="note"
              value={formDataTrialBooking.note}
              onChange={handleInputChangeTrialBooking}
              multiline
              minRows={3}
              error={Boolean(formDataTrialBookingErrors.note)}
              helperText={formDataTrialBookingErrors.note}
            />

            <div className="flex justify-end gap-4">
              <Button
                variant="contained"
                className="!text-white"
                color="error"
                onClick={() => {
                  toggleTrialBooking();
                  setFormDataTrialBooking({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    parent: '',
                    dob: '',
                    subtypeId: '',
                    trialDate: '',
                    note: '',
                  });
                  setTrialDate(null);
                  setTrialDob(null);
                  setFormDataTrialBookingErrors({});
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className="!text-white"
                color="primary"
                onClick={handleSubmitTrialBooking}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </ReuseableModal>
    </>
  );
};

export default QuickActions;
